import { useEffect, useState } from 'react';

/**
 * Per-admin per-table column visibility, persisted in localStorage.
 *
 * Key shape: `sh24.cols.{tableId}.{adminId}` → JSON array of visible column ids.
 * The fallback when nothing is stored is the supplied `defaults` array, so a
 * new admin sees a sensible baseline before they touch anything.
 *
 * Neue Default-Spalten werden in gespeicherte Auswahlen GEMERGT: unter
 * `{key}.known` merken wir, welche Default-Spalten der Admin schon einmal
 * gesehen hat. Kommt eine neue Default-Spalte dazu (Release), wird sie
 * automatisch sichtbar — eine bewusst abgewählte Spalte bleibt abgewählt.
 *
 * NOT backend-persisted yet — switching browsers means re-picking columns.
 * Move to backend admin_preferences when cross-device sync becomes a need.
 */
export function useColumnPreferences(
  tableId: string,
  adminId: number | null | undefined,
  defaults: string[],
): [string[], (next: string[]) => void] {
  const storageKey = adminId != null ? `sh24.cols.${tableId}.${adminId}` : null;

  /** Liest gespeicherte Auswahl + merged neue (unbekannte) Default-Spalten. */
  const hydrate = (): string[] => {
    if (!storageKey || typeof localStorage === 'undefined') return defaults;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return defaults;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed) || parsed.some(x => typeof x !== 'string')) return defaults;
      let visible = parsed as string[];

      // Merge: Default-Spalten, die der Admin noch nie gesehen hat, anhängen.
      const knownRaw = localStorage.getItem(`${storageKey}.known`);
      const known: string[] = knownRaw ? JSON.parse(knownRaw) : [];
      const knownSet = new Set(Array.isArray(known) ? known : []);
      const unseen = defaults.filter(id => !knownSet.has(id) && !visible.includes(id));
      if (unseen.length > 0) {
        visible = [...visible, ...unseen];
        localStorage.setItem(storageKey, JSON.stringify(visible));
      }
      localStorage.setItem(`${storageKey}.known`, JSON.stringify([...new Set([...knownSet, ...defaults])]));
      return visible;
    } catch {
      return defaults;
    }
  };

  const [visible, setVisible] = useState<string[]>(hydrate);

  // Re-hydrate if the admin context changes (e.g. logout/login during dev).
  useEffect(() => {
    setVisible(hydrate());
  }, [storageKey]); // eslint-disable-line react-hooks/exhaustive-deps -- defaults intentionally stable

  const persist = (next: string[]) => {
    setVisible(next);
    if (!storageKey || typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
      // Alles, was der Admin jetzt sieht/abwählt, gilt als bekannt.
      localStorage.setItem(`${storageKey}.known`, JSON.stringify([...new Set([...next, ...defaults])]));
    } catch { /* quota / private mode */ }
  };

  return [visible, persist];
}
